import React, { useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import PersonTimeline from 'domain/shared/timeline/person';
import { type AddTimelineItemInputProps } from 'domain/shared/timeline/person/PersonTimelinePageLayout';
import EditNote from '@mui/icons-material/EditNote';
import MyLocation from '@mui/icons-material/MyLocation';
import { type FormConfigMap } from 'domain/shared/timeline/person/PersonTimelineLayout';
import { StaffingProjectSectionWrapper, useProjectId } from 'domain/shared/project';
import useProjectNoteTimelineRegistry, {
  useProjectCloseoutTimelineRegistry,
  useProjectSummaryReviewTimelineRegistry,
  useProjectSystemNoteTimelineRegistry,
} from './useNoteTimelineRegistry';
import { useProjectTimeline } from './hooks/useProjectTimeline';
import ProjectTimelineRecordTypeFilter from './ProjectTimelineRecordType';
import { ACCESS_INFO_ACTIONS, ACCESS_INFO_SECTIONS, useProjectSecurityContext } from 'domain/shared/newProject';
import { Loading, PlusIcon } from 'framework/ui/mui-builder';
import { PROJECT_TIMELINE_TYPE } from './constants';
import { Filters, useListContext } from 'framework/list';
import { FilterCondition } from 'framework/types';

const PROJECT_TIMELINE_MESSAGE_CONTEXT = 'PROJECT_TIMELINE_MESSAGE_CONTEXT';

const DEFAULT_FILTER = {
  type: {
    [FilterCondition.IN]: [PROJECT_TIMELINE_TYPE.USER_NOTE],
  },
};

const TIMELINE_FILTER = (
  <Filters>
    <ProjectTimelineRecordTypeFilter cleanableFilters={[]} source="type" />
  </Filters>
);

const formConfig: FormConfigMap = {
  AddCloseoutForm: {
    icon: <MyLocation />,
    text: 'Closeout',
  },
  default: {
    icon: <EditNote />,
    text: 'New note',
  },
};

const TimelineActionsButton = ({ registries, selectItemCallback }: AddTimelineItemInputProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { data: { items = [] } = {} } = useListContext();
  const open = Boolean(anchorEl);
  const noteRegistry = registries?.find(r => r.name === 'Note');
  const closeoutRegistry = registries?.find(r => r.name === 'Closeout');

  const hasExistingCloseout = items.some(item => item.type === PROJECT_TIMELINE_TYPE.CLOSEOUT);

  const handleClose = () => setAnchorEl(null);

  const handleAddNote = () => {
    handleClose();
    if (noteRegistry) selectItemCallback(noteRegistry);
  };

  const handleCloseout = () => {
    handleClose();
    if (closeoutRegistry) selectItemCallback(closeoutRegistry);
  };

  return (
    <>
      <IconButton onClick={handleAddNote}>
        <PlusIcon color="primary" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ '& .MuiPaper-root': { minWidth: 160 } }}
      >
        <MenuItem onClick={handleAddNote}>Add note</MenuItem>
        {!hasExistingCloseout && <MenuItem onClick={handleCloseout}>Add closeout</MenuItem>}
      </Menu>
    </>
  );
};

const ProjectTimelinePage = () => {
  const projectId = useProjectId();
  const {
    data: project,
    refetch,
    loading,
    getTimelineItems,
    getTimelinePageById,
    createTimelineItem,
  } = useProjectTimeline(projectId);
  const { isAccessGranted } = useProjectSecurityContext();

  const entityId = project?.id?.toString() ?? '';

  const noteRegistry = useProjectNoteTimelineRegistry({ refetch: () => refetch().then(() => {}) });
  const systemNoteRegistry = useProjectSystemNoteTimelineRegistry();
  const closeoutRegistry = useProjectCloseoutTimelineRegistry({
    projectId: entityId,
    refetch: () => refetch().then(() => {}),
  });
  const summaryReviewRegistry = useProjectSummaryReviewTimelineRegistry();
  const registries = [noteRegistry, systemNoteRegistry, closeoutRegistry, summaryReviewRegistry];

  if (loading || !entityId) return <Loading isInitialPosition />;

  return (
    <StaffingProjectSectionWrapper>
      <PersonTimeline
        entityId={entityId}
        registries={registries}
        getTimelineItems={getTimelineItems}
        createTimelineItem={createTimelineItem}
        getTimelinePageById={getTimelinePageById}
        messageContext={PROJECT_TIMELINE_MESSAGE_CONTEXT}
        formConfig={formConfig}
        AddTimelineItemInput={
          isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_TIMELINE_ENTRY, ACCESS_INFO_ACTIONS.MANAGE) ||
          isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_TIMELINE_ENTRY, ACCESS_INFO_ACTIONS.PARTIAL_MANAGE)
            ? TimelineActionsButton
            : undefined
        }
        noEntriesMessage="No entries in this timeline yet."
        defaultFilter={DEFAULT_FILTER}
        filter={TIMELINE_FILTER}
      />
    </StaffingProjectSectionWrapper>
  );
};

export default ProjectTimelinePage;
